import React, { useEffect, useState, useCallback } from 'react';
import { inventoryAPI, contractsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarDays, Plus, X, Loader2, Play, RefreshCw, ClipboardList, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { LineQtyEditor, PlanDeleteBar, canEditLine } from '@/components/PlanEditControls';

/** Click-through of the work orders holding a material's reserved stock. */
const ReservedBy = ({ m, testId }) => {
  const rows = m.reserved_by || [];
  if (!m.committed) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="block text-[9px] font-normal opacity-80 underline decoration-dotted underline-offset-2 hover:opacity-100">
          {m.on_hand} on hand · {m.committed} reserved by {rows.length || 'open'} WO{rows.length === 1 ? '' : 's'}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[28rem] p-3 text-[11px]" data-testid={`${testId}-panel`}>
        <p className="font-medium text-slate-700 mb-1.5">
          {m.item_name} — reserved{m.source_godown_name ? ` in ${m.source_godown_name}` : ''}
        </p>
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {rows.map(h => (
            <div key={h.wo_id} className="flex items-baseline justify-between gap-3 border-b border-dashed last:border-0 pb-1">
              <span className="min-w-0">
                <span className="font-mono text-slate-700">{h.wo_number || '—'}</span>
                <span className="opacity-70"> · {h.item_name}</span>
                {h.route_card_number
                  ? <span className="block opacity-60">Route card {h.route_card_number} · {h.pending_quantity} pending</span>
                  : <span className="block opacity-60">{h.pending_quantity} pending</span>}
              </span>
              <span className="font-mono shrink-0">{h.reserved} <span className="opacity-60">({h.status})</span></span>
            </div>
          ))}
          {rows.length === 0 && <p className="opacity-70">No open work orders found.</p>}
        </div>
        <p className="mt-2 text-[10px] opacity-70">
          Reserved = each work order's pending quantity converted through its route yield.
          Close or receive a work order to release its stock.
        </p>
      </PopoverContent>
    </Popover>
  );
};

const planStatusStyle = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  empty: 'bg-slate-100 text-slate-500 border-slate-300',
};
const woStatusStyle = {
  draft: 'bg-slate-100 text-slate-700', released: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700', completed: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-emerald-50 text-emerald-700', deleted: 'bg-rose-50 text-rose-700',
};

export default function ProductionPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [reviewId, setReviewId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryAPI.listProductionPlans(includeCompleted);
      setPlans(res.data?.plans || []);
    } finally { setLoading(false); }
  }, [includeCompleted]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    inventoryAPI.getLevel1Items()
      .then(r => setItems(r.data?.items || [])).catch(() => {});
    inventoryAPI.listItems({ limit: 1000, make_or_buy: 'make', sort_by: 'item_name', sort_order: 'asc' })
      .then(r => setAllItems(r.data?.items || r.data || [])).catch(() => {});
    contractsAPI.activeCustomers()
      .then(r => setCustomers(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="p-6 space-y-6" data-testid="production-plans-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarDays className="w-6 h-6 text-indigo-600" /> Daily Production Plan</h1>
          <p className="text-sm text-muted-foreground mt-1">Plan the day's parts & quantities — draft work orders are created for review & release.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={includeCompleted} onCheckedChange={setIncludeCompleted} data-testid="toggle-completed" />
            <Label className="text-xs">Show completed</Label>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="plans-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowBuilder(true)} data-testid="new-plan-btn">
            <Plus className="w-4 h-4 mr-1" /> New Plan
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Live & recent plans</CardTitle></CardHeader>
        <CardContent>
          {loading ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
            : plans.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="plans-empty">No production plans yet. Create one to start.</div>
            : (
              <div className="overflow-x-auto border rounded">
                <table className="w-full text-xs">
                  <thead><tr className="border-b bg-muted/50">
                    <th className="text-left p-2">Plan</th><th className="text-left p-2">Date</th><th className="text-left p-2">Customer</th>
                    <th className="text-center p-2">Parts</th><th className="text-center p-2">Draft</th><th className="text-center p-2">Live</th>
                    <th className="text-center p-2">Done</th><th className="text-center p-2">Status</th><th className="text-center p-2">Action</th>
                  </tr></thead>
                  <tbody>
                    {plans.map((p, i) => (
                      <tr key={p.id} className="border-b hover:bg-muted/30" data-testid={`plan-row-${i}`}>
                        <td className="p-2 font-mono font-medium">{p.plan_number}</td>
                        <td className="p-2">{p.plan_date}</td>
                        <td className="p-2">{p.customer_name}</td>
                        <td className="p-2 text-center">{p.total_wos}</td>
                        <td className="p-2 text-center">{p.draft_wos}</td>
                        <td className="p-2 text-center text-blue-700 font-medium">{p.live_wos}</td>
                        <td className="p-2 text-center text-emerald-700">{p.completed_wos}</td>
                        <td className="p-2 text-center"><Badge variant="outline" className={`text-[10px] ${planStatusStyle[p.status] || ''}`}>{p.status}</Badge></td>
                        <td className="p-2 text-center">
                          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setReviewId(p.id)} data-testid={`plan-review-${i}`}>Review</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </CardContent>
      </Card>

      {showBuilder && (
        <PlanBuilder items={items} allItems={allItems} customers={customers}
          onClose={() => setShowBuilder(false)} onDone={() => { setShowBuilder(false); load(); }} />
      )}
      {reviewId && (
        <PlanReview planId={reviewId} onClose={() => setReviewId(null)} onChanged={load} />
      )}
    </div>
  );
}

function PlanBuilder({ items, allItems, customers, onClose, onDone }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [customerId, setCustomerId] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState([{ item_id: '', quantity: '', route_id: '', route_card_number: '', info: null, loading: false }]);
  const [routeCard, setRouteCard] = useState('');
  const [saving, setSaving] = useState(false);

  const [showAllParts, setShowAllParts] = useState(false);
  const visibleItems = showAllParts ? (allItems || []) : (items || []);
  const itemOptions = visibleItems.map(i => ({ value: i.id, label: `${i.unique_id} - ${i.item_name}`, sub: i.item_type || '', searchStr: `${i.unique_id} ${i.item_name}` }));
  const customerName = (customers.find(c => c.id === customerId) || {}).customer_name || '';

  const fetchInfo = async (idx, item_id, quantity, route_id) => {
    if (!item_id || !(parseFloat(quantity) > 0)) return;
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, loading: true } : r));
    try {
      const res = await inventoryAPI.getPartPlanInfo(item_id, parseFloat(quantity), route_id || undefined);
      setRows(prev => prev.map((r, i) => i === idx
        ? { ...r, info: res.data, route_id: res.data?.route?.id || '', loading: false } : r));
    } catch { setRows(prev => prev.map((r, i) => i === idx ? { ...r, loading: false } : r)); }
  };

  const loadRoutes = async (idx, item_id, quantity) => {
    if (!item_id) return;
    try {
      const res = await inventoryAPI.getItemRoutes(item_id);
      const list = res.data?.routes || [];
      const def = (list.find(r => r.is_default) || list[0] || {}).id || '';
      setRows(prev => prev.map((r, i) => i === idx ? { ...r, routes: list, route_id: def } : r));
      if (parseFloat(quantity) > 0) fetchInfo(idx, item_id, quantity, def);
    } catch { /* dropdown stays empty */ }
  };

  const setRow = (idx, patch) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const submit = async () => {
    if (!customerId) { toast.error('Select a customer'); return; }
    const valid = rows.filter(r => r.item_id && parseFloat(r.quantity) > 0);
    if (valid.length === 0) { toast.error('Add at least one part with quantity'); return; }
    const missingRc = valid.filter(r => !(r.route_card_number || '').trim() && !routeCard.trim());
    if (missingRc.length) {
      toast.error('Route card number is mandatory — enter one for the plan or for every line');
      return;
    }
    setSaving(true);
    try {
      const res = await inventoryAPI.createProductionPlan({
        plan_date: date, customer_id: customerId, customer_name: customerName, notes,
        route_card_number: routeCard,
        lines: valid.map(r => ({
          item_id: r.item_id, quantity: parseFloat(r.quantity),
          route_id: r.route_id || null,
          route_card_number: r.route_card_number || '',
        })),
      });
      const sk = res.data?.skipped || [];
      toast.success(`Plan ${res.data.plan_number} created — ${res.data.total_wos} draft WO(s)${sk.length ? `, ${sk.length} skipped` : ''}`);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create plan'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="plan-builder-dialog">
        <DialogHeader>
          <DialogTitle>New Production Plan</DialogTitle>
          <DialogDescription>Pick the date & customer, then add parts and quantities. A draft work order is created per part.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Plan date</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-9 text-sm" data-testid="plan-date" /></div>
          <div><Label className="text-xs">Customer</Label>
            <SearchableSelect options={customers.map(c => ({ value: c.id, label: c.customer_name, searchStr: c.customer_name }))}
              value={customerId} onSelect={setCustomerId} placeholder="Select customer..." testId="plan-customer" /></div>
        </div>

        <div className="flex items-center justify-between gap-3 rounded border bg-slate-50/60 px-2.5 py-1.5">
          <p className="text-[11px] text-muted-foreground">
            {showAllParts
              ? `Showing all ${(allItems || []).length} manufactured part(s) — ${(items || []).length} are level-1; the rest are normally planned through the Dispatch Plan.`
              : `Showing ${(items || []).length} level-1 part(s) — made directly from sheet / raw material / scrap. Parts built from other parts or bought-out components are planned via the Dispatch Plan.`}
          </p>
          <label className="flex items-center gap-2 text-[11px] cursor-pointer shrink-0">
            <input type="checkbox" checked={showAllParts} onChange={e => setShowAllParts(e.target.checked)}
              className="h-3.5 w-3.5 accent-indigo-600" data-testid="plan-show-all-parts" />
            Show all parts
          </label>
        </div>

        <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
          {rows.map((r, idx) => (
            <div key={idx} className="border rounded p-2 space-y-1.5" data-testid={`plan-row-builder-${idx}`}>
              <div className="flex items-end gap-2">
                <div className="flex-1"><Label className="text-[10px]">Part</Label>
                  <SearchableSelect options={itemOptions} value={r.item_id}
                    onSelect={(v) => { setRow(idx, { item_id: v, info: null, route_id: '', routes: [] }); loadRoutes(idx, v, r.quantity); }}
                    placeholder="Select part..." testId={`plan-part-${idx}`} /></div>
                <div className="w-28"><Label className="text-[10px]">Quantity</Label>
                  <Input type="number" step="any" value={r.quantity}
                    onChange={e => setRow(idx, { quantity: e.target.value })}
                    onBlur={() => fetchInfo(idx, r.item_id, r.quantity, r.route_id)}
                    className="h-9 text-sm" data-testid={`plan-qty-${idx}`} /></div>
                <div className="w-32"><Label className="text-[10px]">Route card no.</Label>
                  <Input value={r.route_card_number || ''}
                    onChange={e => setRow(idx, { route_card_number: e.target.value })}
                    placeholder={routeCard || 'optional'}
                    className="h-9 text-sm" data-testid={`plan-route-card-${idx}`} /></div>
                <Button variant="ghost" size="sm" className="h-9 text-rose-600" onClick={() => setRows(rows.filter((_, i) => i !== idx))} data-testid={`plan-remove-${idx}`}><X className="w-4 h-4" /></Button>
              </div>
              <div>
                <Label className="text-[10px]">BOM / Route to consume</Label>
                <SearchableSelect
                  options={(r.routes || r.info?.routes || []).map(rt => ({
                    value: rt.id,
                    label: `${rt.route_name}${rt.revision ? ` rev ${rt.revision}` : ''} · ${rt.material_source_label}${rt.is_default ? ' (default)' : ''}`,
                    sub: rt.bom_summary ? `consumes: ${rt.bom_summary}${rt.bom_count > 3 ? ` +${rt.bom_count - 3} more` : ''}` : 'no materials on BOM',
                    searchStr: `${rt.route_name} ${rt.material_source_label} ${rt.bom_summary || ''}`,
                  }))}
                  value={r.route_id}
                  onSelect={(v) => { setRow(idx, { route_id: v }); fetchInfo(idx, r.item_id, r.quantity, v); }}
                  placeholder={r.item_id ? ((r.routes || []).length ? 'Select BOM...' : 'No active BOM for this part') : 'Select a part first'}
                  testId={`plan-bom-${idx}`} />
                {(r.routes || []).length > 1 && (
                  <p className="text-[9px] text-muted-foreground mt-0.5">
                    {r.routes.length} BOMs available — switching here only affects this plan, the item's default BOM is unchanged.
                  </p>
                )}
              </div>
              {r.loading && <div className="text-[10px] text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> checking stock…</div>}
              {r.info && r.info.route && (
                <div className="text-[10px] bg-muted/40 rounded p-1.5">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-muted-foreground">Route: {r.info.route.route_name}</span>
                    {r.info.any_short
                      ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[9px] flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" /> Short RM</Badge>
                      : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" /> RM available</Badge>}
                    <span className="ml-auto font-medium text-slate-700" data-testid={`plan-max-producible-${idx}`}>Max producible: {r.info.max_producible ?? 0}</span>
                  </div>
                  {r.info.materials.map((m, mi) => (
                    <div key={mi} className={`flex justify-between ${m.shortage > 0 ? 'text-rose-600' : 'text-muted-foreground'}`}>
                      <span>{m.item_name}
                        {m.source_godown_name && <span className="text-[9px] opacity-70"> · from {m.source_godown_name}</span>}
                      </span>
                      <span className="font-mono">need {m.required} / avl {m.net_available}{m.shortage > 0 ? ` (short ${m.shortage})` : ''}
                        <ReservedBy m={m} testId={`plan-reserved-${idx}-${mi}`} />
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {r.info && !r.info.route && <div className="text-[10px] text-rose-600">No active route/BOM for this part — it will be skipped.</div>}
            </div>
          ))}
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setRows([...rows, { item_id: '', quantity: '', route_id: '', route_card_number: '', info: null }])} data-testid="plan-add-row">
            <Plus className="w-3 h-3 mr-1" /> Add part
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Route card no. * (applies to all lines)</Label>
            <Input value={routeCard} onChange={e => setRouteCard(e.target.value)} className="h-9 text-sm"
              placeholder="e.g. RC-2026-0145" data-testid="plan-route-card" /></div>
          <div><Label className="text-xs">Notes (optional)</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} className="h-9 text-sm" data-testid="plan-notes" /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={submit} disabled={saving} data-testid="plan-create-submit">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />} Create draft WOs
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanReview({ planId, onClose, onChanged }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await inventoryAPI.getProductionPlan(planId);
    setPlan(res.data);
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const showRmWarnings = (warnings) => {
    (warnings || []).forEach(w => {
      const holders = (w.holders || []).slice(0, 4).map(h => h.wo_number + (h.plan ? ` (${h.plan})` : '')).join(', ');
      toast.warning(
        `RM over-committed: ${w.item_name} — over by ${w.over_by} (stock ${w.available}, committed ${w.committed}).` +
        (holders ? ` Also reserved by: ${holders}` : ''),
        { duration: 9000 }
      );
    });
  };

  const releaseOne = async (woId) => {
    setBusy(true);
    try { const r = await inventoryAPI.updateWorkOrder(woId, { status: 'released' }); toast.success('Work order released'); showRmWarnings(r.data?.rm_warnings); await load(); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to release'); }
    finally { setBusy(false); }
  };
  const releaseAll = async () => {
    setBusy(true);
    try { const r = await inventoryAPI.releaseProductionPlan(planId); toast.success(r.data.message); showRmWarnings(r.data?.rm_warnings); await load(); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const releaseAndReceive = async () => {
    if (!window.confirm('Release all draft work orders and immediately receive their full quantity? '
      + 'This consumes the BOM material and closes the work orders.')) return;
    setBusy(true);
    try {
      const r = await inventoryAPI.releaseAndReceiveProductionPlan(planId);
      toast.success(r.data.message, { duration: 8000 });
      (r.data.failures || []).forEach(f =>
        toast.error(`${f.wo_number} ${f.item_name}: ${f.reason}`, { duration: 12000 }));
      await load(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Release & receive failed', { duration: 10000 }); }
    finally { setBusy(false); }
  };
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const hasDrafts = plan?.lines?.some(l => l.wo_status === 'draft');
  const hasOpen = plan?.lines?.some(l => !['completed', 'closed', 'cancelled'].includes(l.wo_status));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="plan-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{plan?.plan_number} <Badge variant="outline" className={`text-[10px] ${planStatusStyle[plan?.status] || ''}`}>{plan?.status}</Badge></DialogTitle>
          <DialogDescription>{plan?.plan_date} · {plan?.customer_name} · Review RM consumption and release the draft work orders.</DialogDescription>
        </DialogHeader>

        {!plan ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div> : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {plan.lines.map((ln, i) => (
              <div key={i} className="border rounded p-2" data-testid={`review-line-${i}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-medium">{ln.item_name}</span>
                    <span className="text-[10px] font-mono text-muted-foreground ml-1.5">×{ln.quantity}</span>
                    <span className="text-[10px] text-muted-foreground ml-1.5">({ln.wo_number})</span>
                    {ln.route_name && (
                      <span className="text-[10px] text-slate-600 ml-1.5" data-testid={`review-route-${i}`}>
                        BOM: {ln.route_name}
                      </span>
                    )}
                    {ln.route_card_number && (
                      <span className="text-[10px] text-indigo-700 ml-1.5" data-testid={`review-route-card-${i}`}>
                        RC: {ln.route_card_number}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-[10px] ${woStatusStyle[ln.wo_status] || ''}`}>{ln.wo_status}</Badge>
                    {['completed', 'closed'].includes(ln.wo_status) && (
                      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-0.5" data-testid={`review-line-closed-${i}`}>
                        <CheckCircle2 className="w-2.5 h-2.5" /> Line closed
                      </Badge>
                    )}
                    {ln.wo_status === 'draft' && (
                      <Button size="sm" className="h-6 text-[10px] bg-blue-600 hover:bg-blue-700" disabled={busy}
                        onClick={() => releaseOne(ln.wo_id)} data-testid={`review-release-${i}`}>
                        <Play className="w-3 h-3 mr-0.5" /> Release
                      </Button>
                    )}
                  </div>
                </div>
                {(ln.produced_quantity > 0 || canEditLine(ln, isAdmin)) && (
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {ln.produced_quantity > 0 && (
                      <span className="text-[10px] text-slate-600" data-testid={`review-produced-${i}`}>
                        produced {ln.produced_quantity}/{ln.quantity}
                      </span>
                    )}
                    {canEditLine(ln, isAdmin) && (
                      <LineQtyEditor planId={planId} index={i} line={ln} onSaved={async () => { await load(); onChanged(); }} />
                    )}
                  </div>
                )}
                {(ln.materials || []).length > 0 && !['completed', 'closed', 'deleted', 'cancelled'].includes(ln.wo_status) && (
                  <div className="mt-1 text-[10px]">
                    <div className="flex items-center justify-between mb-0.5">
                      {ln.any_short
                        ? <span className="text-rose-600 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> Raw material short</span>
                        : <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5" /> RM available</span>}
                      <span className="font-medium text-slate-700" data-testid={`review-max-producible-${i}`}>Max producible: {ln.max_producible ?? 0}</span>
                    </div>
                    {ln.materials.map((m, mi) => (
                      <div key={mi} className={`flex justify-between ${m.shortage > 0 ? 'text-rose-600' : 'text-muted-foreground'}`}>
                        <span>{m.item_name}{m.make_or_buy === 'make' ? ' (make)' : ''}</span>
                        <span className="font-mono">need {m.required} / avl {m.net_available}{m.shortage > 0 ? ` (short ${m.shortage})` : ''}
                        <ReservedBy m={m} testId={`review-reserved-${i}-${mi}`} />
                      </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {plan && <PlanDeleteBar plan={plan} isAdmin={isAdmin} onDeleted={() => { onChanged(); onClose(); }} />}
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          {hasDrafts && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={releaseAll} disabled={busy} data-testid="release-all-btn">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />} Release all ready
            </Button>
          )}
          {hasOpen && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={releaseAndReceive} disabled={busy} data-testid="release-receive-btn">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Release &amp; receive
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
